import type { StructuredMessage, StructuredRestaurantItem } from '../types/structuredMessage';

/**
 * 结构化文本解析器
 * 从文本中提取序号、餐厅名称、描述等结构化信息
 */

/**
 * 从文本中提取所有餐厅项
 * 支持格式：
 * - 1. **餐厅名称**：描述
 * - 1. **餐厅名称**\n\n描述
 * - 1. 餐厅名称：描述（自动加粗）
 */
export function extractRestaurantItems(text: string): StructuredRestaurantItem[] {
  const items: StructuredRestaurantItem[] = [];
  
  if (!text || text.trim().length === 0) {
    return items;
  }

  // 匹配模式1: 数字. **餐厅名**：描述
  // 匹配模式2: 数字. **餐厅名**\n\n描述
  // 匹配模式3: 数字. 餐厅名：描述（自动加粗）
  // 匹配模式4: 数字. **餐厅名**（只有名称，没有描述）
  // 匹配模式5: 数字. 餐厅名（只有名称，没有冒号，没有描述）- 只匹配到行尾
  const patterns = [
    // 模式1: 数字. **餐厅名**：描述（同一行）
    /(\d+)[\.、]\s*\*\*([^*]+?)\*\*[：:]\s*([^\n]+?)(?=\n\d+[\.、]|\n\n|\*\*|$)/gs,
    // 模式2: 数字. **餐厅名**\n\n描述（多行）
    /(\d+)[\.、]\s*\*\*([^*]+?)\*\*\n\n([^\n]+(?:\n(?!\d+[\.、]|\*\*)[^\n]*)*?)(?=\n\d+[\.、]|\n\n\*\*|$)/gs,
    // 模式3: 数字. 餐厅名：描述（自动加粗）
    /(\d+)[\.、]\s*([^：:\*\n]+?)[：:]\s*([^\n]+?)(?=\n\d+[\.、]|\n\n|\*\*|$)/gs,
    // 模式4: 数字. **餐厅名**（只有名称，没有描述）- 匹配到下一个序号或段落结束
    /(\d+)[\.、]\s*\*\*([^*]+?)\*\*(?=\n\d+[\.、]|\n\n|$)/gs,
    // 模式5: 数字. 餐厅名（只有名称，没有冒号，没有描述）- 只匹配到行尾（\n）或字符串结尾
    // 注意：这个模式应该最后匹配，避免覆盖有描述的情况
    // 限制：只匹配到换行符或下一个序号，不匹配同一行中的后续文本
    // 使用负向前瞻确保不匹配有冒号的情况（模式3已处理）
    /(\d+)[\.、]\s*([^：:\*\n]+?)(?=\n\d+[\.、]|\n\n|\n|$)/gs,
  ];

  const processedIndices = new Set<number>();

  for (const pattern of patterns) {
    let match;
    while ((match = pattern.exec(text)) !== null) {
      const index = parseInt(match[1], 10);
      
      // 避免重复处理同一个序号
      if (processedIndices.has(index)) {
        continue;
      }

      const restaurantName = match[2]?.trim() || '';
      // 对于模式4和5，description可能不存在（match[3]）
      const description = match[3]?.trim() || '';

      // 只要有餐厅名称就提取，描述可以为空
      if (restaurantName) {
        items.push({
          index,
          restaurantName,
          description,
        });
        processedIndices.add(index);
      }
    }
  }

  // 按序号排序
  items.sort((a, b) => a.index - b.index);

  return items;
}

/**
 * 解析结构化文本
 * 提取介绍文本、餐厅列表和结尾文本
 */
export function parseStructuredText(text: string): StructuredMessage {
  if (!text || text.trim().length === 0) {
    return {
      restaurants: [],
    };
  }

  // 提取所有餐厅项
  const restaurants = extractRestaurantItems(text);

  if (restaurants.length === 0) {
    // 如果没有找到餐厅项，返回整个文本作为介绍文本
    return {
      introText: text.trim(),
      restaurants: [],
    };
  }

  // 找到第一个餐厅项的位置
  const firstRestaurantPattern = new RegExp(
    `(${restaurants[0].index}[\.、]\\s*\\*\\*${restaurants[0].restaurantName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\*\\*|${restaurants[0].index}[\.、]\\s*${restaurants[0].restaurantName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
    'i'
  );
  const firstMatch = text.search(firstRestaurantPattern);
  
  // 找到最后一个餐厅项的位置和结束位置
  const lastRestaurant = restaurants[restaurants.length - 1];
  const lastRestaurantPattern = new RegExp(
    `(${lastRestaurant.index}[\.、]\\s*\\*\\*${lastRestaurant.restaurantName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\*\\*|${lastRestaurant.index}[\.、]\\s*${lastRestaurant.restaurantName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
    'i'
  );
  const lastMatch = text.search(lastRestaurantPattern);
  
  // 找到最后一个餐厅项描述结束的位置
  let lastRestaurantEnd = lastMatch;
  if (lastMatch !== -1) {
    // 从最后一个餐厅项开始，找到描述结束的位置
    const afterLastMatch = text.substring(lastMatch);
    // 查找下一个序号或结尾
    const nextItemMatch = afterLastMatch.search(/\n\d+[\.、]\s/);
    if (nextItemMatch !== -1) {
      lastRestaurantEnd = lastMatch + nextItemMatch;
    } else {
      // 找到描述结束的位置（双换行或文本结尾）
      const descEndMatch = afterLastMatch.search(/\n\n(?!\d+[\.、])/);
      if (descEndMatch !== -1) {
        lastRestaurantEnd = lastMatch + descEndMatch + 2;
      } else {
        lastRestaurantEnd = text.length;
      }
    }
  }

  // 提取介绍文本（第一个餐厅项之前的内容）
  const introText = firstMatch > 0 ? text.substring(0, firstMatch).trim() : undefined;
  
  // 提取结尾文本（最后一个餐厅项之后的内容）
  const outroText = lastRestaurantEnd < text.length 
    ? text.substring(lastRestaurantEnd).trim() 
    : undefined;

  return {
    introText: introText || undefined,
    restaurants,
    outroText: outroText || undefined,
  };
}

/**
 * 解析结构化消息（仅支持文本格式）
 * 版本1.2.5: 移除JSON解析功能，仅使用文本解析
 */
export function parseStructuredMessage(text: string): StructuredMessage {
  // 版本1.2.5: 直接使用文本解析，不再支持JSON格式
  return parseStructuredText(text);
}

// 版本1.2.6: 添加流式JSON代码块解析函数

/**
 * 检测文本中是否包含JSON代码块标记
 * 返回代码块状态和内容
 */
export function detectJsonCodeBlock(text: string): {
  hasCodeBlock: boolean;
  isOpening: boolean;
  isClosing: boolean;
  jsonContent: string;
  remainingText: string;
} {
  const jsonBlockPattern = /```json\s*\n?([\s\S]*?)```/g;
  const openingPattern = /```json\s*\n?/;
  const closingPattern = /```/;
  
  let hasCodeBlock = false;
  let isOpening = false;
  let isClosing = false;
  let jsonContent = '';
  let remainingText = text;
  
  // 检查是否有完整的代码块
  const fullMatch = text.match(jsonBlockPattern);
  if (fullMatch) {
    // 提取JSON内容
    const match = text.match(/```json\s*\n?([\s\S]*?)```/);
    if (match && match[1]) {
      jsonContent = match[1].trim();
      remainingText = text.replace(match[0], '').trim();
      hasCodeBlock = true;
      isOpening = true;
      isClosing = true;
    }
  } else {
    // 检查是否有开始标记
    if (openingPattern.test(text)) {
      hasCodeBlock = true;
      isOpening = true;
      // 提取开始标记后的内容
      const afterOpening = text.split(/```json\s*\n?/)[1] || '';
      // 检查是否有结束标记
      if (closingPattern.test(afterOpening)) {
        isClosing = true;
        const match = afterOpening.match(/([\s\S]*?)```/);
        if (match && match[1]) {
          jsonContent = match[1].trim();
          remainingText = text.replace(/```json\s*\n?[\s\S]*?```/, '').trim();
        }
      } else {
        // 只有开始标记，没有结束标记
        jsonContent = afterOpening;
        remainingText = text.replace(/```json\s*\n?/, '').trim();
      }
    }
  }
  
  return {
    hasCodeBlock,
    isOpening,
    isClosing,
    jsonContent,
    remainingText
  };
}

/**
 * 检测字符串中是否包含完整的 JSON 对象
 * 通过计算大括号的匹配来判断
 */
export function isCompleteJsonInCodeBlock(str: string): boolean {
  if (!str || !str.trim().startsWith('{')) {
    return false;
  }

  let depth = 0;
  let inString = false;
  let escapeNext = false;

  for (let i = 0; i < str.length; i++) {
    const char = str[i];

    if (escapeNext) {
      escapeNext = false;
      continue;
    }

    if (char === '\\') {
      escapeNext = true;
      continue;
    }

    if (char === '"') {
      inString = !inString;
      continue;
    }

    if (inString) continue;

    if (char === '{') {
      depth++;
    } else if (char === '}') {
      depth--;
      if (depth === 0) {
        // 找到完整的 JSON 对象
        return true;
      }
    }
  }

  return false;
}

/**
 * 从流式缓冲区中提取完整的 JSON 对象
 * 返回 { json: 解析后的对象, remaining: 剩余文本 } 或 null
 */
export function extractCompleteJsonFromStream(
  buffer: string,
  isInCodeBlock: boolean
): {
  json: StructuredMessage | null;
  convertedText: string;
  remainingBuffer: string;
  stillInCodeBlock: boolean;
} {
  if (!isInCodeBlock || !buffer || !buffer.trim().startsWith('{')) {
    return {
      json: null,
      convertedText: '',
      remainingBuffer: buffer,
      stillInCodeBlock: isInCodeBlock
    };
  }

  let depth = 0;
  let inString = false;
  let escapeNext = false;
  let startIndex = -1;

  for (let i = 0; i < buffer.length; i++) {
    const char = buffer[i];

    if (escapeNext) {
      escapeNext = false;
      continue;
    }

    if (char === '\\') {
      escapeNext = true;
      continue;
    }

    if (char === '"') {
      inString = !inString;
      continue;
    }

    if (inString) continue;

    if (char === '{') {
      if (startIndex === -1) {
        startIndex = i;
      }
      depth++;
    } else if (char === '}') {
      depth--;
      if (depth === 0 && startIndex !== -1) {
        // 找到完整的 JSON 对象
        const jsonStr = buffer.substring(startIndex, i + 1);
        const remaining = buffer.substring(i + 1);
        
        try {
          const json = JSON.parse(jsonStr);
          
          // 验证是否为StructuredMessage格式
          if (json && typeof json === 'object' && Array.isArray(json.restaurants)) {
            const structuredMessage: StructuredMessage = {
              introText: json.introText || undefined,
              restaurants: json.restaurants.map((item: any) => ({
                index: item.index || 0,
                restaurantName: item.restaurantName || item.name || '',
                description: item.description || '',
                restaurant: item.restaurant || undefined,
              })),
              outroText: json.outroText || undefined,
            };
            
            // 转换为自然语言格式
            const convertedText = formatStructuredMessageToText(structuredMessage);
            
            // 检查是否还有代码块标记
            const stillInCodeBlock = remaining.trim().length > 0 && !remaining.trim().startsWith('```');
            
            return {
              json: structuredMessage,
              convertedText,
              remainingBuffer: remaining,
              stillInCodeBlock
            };
          } else {
            // 不是StructuredMessage格式
            return {
              json: null,
              convertedText: '',
              remainingBuffer: buffer,
              stillInCodeBlock: true
            };
          }
        } catch (e) {
          // JSON 解析失败
          return {
            json: null,
            convertedText: '',
            remainingBuffer: buffer,
            stillInCodeBlock: true
          };
        }
      }
    }
  }

  // 未找到完整的JSON
  return {
    json: null,
    convertedText: '',
    remainingBuffer: buffer,
    stillInCodeBlock: true
  };
}

/**
 * 移除JSON代码块标记（```json 和 ```）
 */
export function removeJsonCodeBlockMarkers(text: string): string {
  return text
    .replace(/```json\s*\n?/g, '')
    .replace(/```\s*\n?/g, '')
    .trim();
}

/**
 * 将结构化消息转换为自然语言格式的文本
 * 格式：introText + 序号. **餐厅名称**\n描述 + outroText
 */
export function formatStructuredMessageToText(structuredData: StructuredMessage): string {
  if (!structuredData || !structuredData.restaurants || structuredData.restaurants.length === 0) {
    // 如果没有餐厅，返回介绍文本或结尾文本
    return structuredData.introText || structuredData.outroText || '';
  }

  const parts: string[] = [];

  // 添加介绍文本
  if (structuredData.introText) {
    parts.push(structuredData.introText.trim());
  }

  // 添加餐厅列表
  structuredData.restaurants.forEach((item) => {
    const restaurantLine = `${item.index}. **${item.restaurantName}**`;
    if (item.description) {
      parts.push(restaurantLine);
      parts.push(item.description.trim());
    } else {
      parts.push(restaurantLine);
    }
  });

  // 添加结尾文本
  if (structuredData.outroText) {
    parts.push(structuredData.outroText.trim());
  }

  return parts.join('\n\n');
}

